import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

import type { Slicers } from 'types/services';
import { SLICER_KEYS } from 'types/services';
import type { TableQueryType } from 'types';
import type { ColumnPinningState, VisibilityState, GroupingState } from '@tanstack/react-table';
import type { ColumnAggregationState } from 'types/globalStore.ts';

const initialState: TableQueryType<any, any, undefined> = {};

export const tablesQueryState = createSlice({
  name: SLICER_KEYS.TABLE_QUERY,
  initialState,
  reducers: {
    setTableQuery: (state, action: PayloadAction<TableQueryType<any, any, undefined>>) => {
      const key = Object.keys(action.payload)[0];

      if (key) {
        state[key] = action.payload[key];
      }
    },
    resetTableQuery: () => initialState,
    updateTableQuery: (state, action: PayloadAction<TableQueryType<any, any, undefined>>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    removeTableQuery: (state, action: PayloadAction<string>) => {
      delete state[action.payload];
    },
    updateTablePagination: (
      state,
      action: PayloadAction<{
        key: string;
        pagination: TableQueryType<any, any>[string]['pagination'];
      }>
    ) => {
      const { key, pagination } = action.payload;

      if (state[key]) {
        state[key].pagination = pagination;
      }
    },
    updateTableSort: (
      state,
      action: PayloadAction<{
        key: string;
        sort: { by: string; order: 'asc' | 'desc' };
      }>
    ) => {
      const { key, sort } = action.payload;

      if (state[key]) {
        state[key].sort = sort;
      }
    },
    updateTableSearch: (state, action: PayloadAction<{ key: string; search: string }>) => {
      const { key, search } = action.payload;

      if (state[key]) {
        state[key].search = search;
        state[key].pagination.page = 1;
      }
    },
    updateTableFilters: (
      state,
      action: PayloadAction<{
        key: string;
        filters: { [key: string]: string };
      }>
    ) => {
      const { key, filters } = action.payload;
      if (state[key]) {
        state[key].filters = filters;
        state[key].pagination.page = 1;
      }
    },
    updateCachedData: (state, action: PayloadAction<{ key: string; data: any }>) => {
      const { key, data } = action.payload;

      if (state[key] && 'cachedData' in state[key]) {
        state[key].cachedData = data;
      }
    },
    updateTableColumnVisibility: (state, action: PayloadAction<{ key: string; columnVisibility: VisibilityState }>) => {
      const { key, columnVisibility } = action.payload;
      if (!state[key]) {
        state[key] = { pagination: { page: 1, pageSize: 10 }, filters: {}, sort: { by: '', order: 'asc' } };
      }
      state[key].columnVisibility = columnVisibility;
    },
    updateTableColumnPinning: (state, action: PayloadAction<{ key: string; columnPinning: ColumnPinningState }>) => {
      const { key, columnPinning } = action.payload;
      if (!state[key]) {
        state[key] = { pagination: { page: 1, pageSize: 10 }, filters: {}, sort: { by: '', order: 'asc' } };
      }
      state[key].columnPinning = columnPinning;
    },
    updateTableColumnGrouping: (state, action: PayloadAction<{ key: string; columnGrouping: GroupingState }>) => {
      const { key, columnGrouping } = action.payload;
      if (!state[key]) {
        state[key] = { pagination: { page: 1, pageSize: 10 }, filters: {}, sort: { by: '', order: 'asc' } };
      }
      state[key].columnGrouping = columnGrouping;
    },
    updateTableColumnAggregation: (state, action: PayloadAction<{ key: string; columnAggregation: ColumnAggregationState }>) => {
      const { key, columnAggregation } = action.payload;
      if (!state[key]) {
        state[key] = { pagination: { page: 1, pageSize: 10 }, filters: {}, sort: { by: '', order: 'asc' } };
      }
      state[key].columnAggregation = columnAggregation;
    },
  },
});

export const {
  setTableQuery,
  resetTableQuery,
  updateTableQuery,
  removeTableQuery,
  updateTablePagination,
  updateTableSort,
  updateTableSearch,
  updateTableFilters,
  updateCachedData,
  updateTableColumnVisibility,
  updateTableColumnPinning,
  updateTableColumnGrouping,
  updateTableColumnAggregation,
} = tablesQueryState.actions;

export const useSetTableQuery = () => {
  const dispatch = useDispatch();
  return (payload: TableQueryType<any, any>) => dispatch(setTableQuery(payload));
};

export const useResetTableQuery = () => {
  const dispatch = useDispatch();
  return () => dispatch(resetTableQuery());
};

export const useUpdateTableQuery = () => {
  const dispatch = useDispatch();
  return (payload: TableQueryType<any, any>) => dispatch(updateTableQuery(payload));
};

export const useRemoveTableQuery = () => {
  const dispatch = useDispatch();
  return (payload: string) => dispatch(removeTableQuery(payload));
};

export const useGetTablesQueries = () => {
  const selector = useSelector((state: Slicers) => state[SLICER_KEYS.TABLE_QUERY]);

  return selector;
};

export const useGetTableQuery = (key: string) => {
  const selector = useSelector((state: Slicers) => {
    const filters = state[SLICER_KEYS.TABLE_QUERY];

    if (!filters) return undefined;
    if (!filters[key]) return undefined;

    return filters[key];
  });

  return selector;
};

export const selectGroupedTableIds = createSelector(
  (state: Slicers) => state[SLICER_KEYS.TABLE_QUERY],
  (tableQuery) => {
    if (!tableQuery) return [];
    return Object.entries(tableQuery)
      .filter(([, value]) => (value?.columnGrouping?.length ?? 0) > 0)
      .map(([key]) => key);
  }
);
